import useJConfirmationBar from '@/hooks/useJConfirmationBar';
import Jpaper from '@atoms/JPaper';
import { JTable } from '@atoms/JTable';
import { JTablePagination } from '@atoms/JTablePagination';
import { ConsumptionAction } from '@constants';
import RechargeTableRow from '@enterprise/components/RechargeTableRow';
import SpendingTableRow from '@enterprise/components/SpendingTableRow';
import { useUpdateConsumptionAllowedActions } from '@enterprise/mutations/enterpriseMutations';
import {
  useGetEnterpriseCreditsConsumption,
  useGetEnterpriseCreditsTopUp,
} from '@enterprise/queries/useGetEnterprises';
import { KEY_ORDER } from '@models/key-order';
import TableColumn from '@models/table-column';
import { Box } from '@mui/material';
import EndPoints from '@services/endpoints';
import CheckboxSetting from '@settings/components/atoms/CheckboxSetting';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
interface EnterpriseCreditsProps {
  id: string | undefined;
  consumptionAllowedActions: ConsumptionAction[] | undefined;
}

const SPENDING_TABLE_COLS: TableColumn[] = [
  {
    name: 'ESTABLISHMENT',
    orderable: true,
    sortingField: 'establishment_name',
  },
  {
    name: 'USER',
    orderable: true,
    sortingField: 'fullname',
  },
  {
    name: 'CREDIT',
    orderable: true,
    sortingField: 'creditConsumption_credits_amount_consumed',
  },
  {
    name: 'ACTION',
    orderable: true,
    sortingField: 'creditConsumption_consumption_action',
  },

  {
    name: 'DATE_CONSUMPTION',
    orderable: true,
    sortingField: 'creditConsumption_created_at',
  },
];
const RECHARGE_TABLE_COLS: TableColumn[] = [
  {
    name: 'DATE_RECHARGE',
    orderable: true,
    sortingField: 'createdAt',
  },
  {
    name: 'PAID_AMOUNT',
    orderable: true,
    sortingField: 'eurosAmount',
  },
  {
    name: 'CREDIT',
    orderable: true,
    sortingField: 'creditsAmount',
  },
  { name: 'DISTRIBUTION', orderable: true, sortingField: 'status' },

  { name: 'ESTABLISHMENT', orderable: false, sortingField: '' },
];
const EnterpriseCredits = ({
  id,
  consumptionAllowedActions,
}: EnterpriseCreditsProps) => {
  const PER_PAGE = 10;
  const RECHARGE_PER_PAGE = 3;
  const { t } = useTranslation();
  const [page, setPage] = useState<number>(1);
  const [pageRecharge, setPageRecharge] = useState<number>(1);
  const ENTERPRISE_DEFAULT_ORDER = {
    order: KEY_ORDER.DESC,
    orderBy: 'establishment_name',
  };
  const RECHARGE_DEFAULT_ORDER = {
    order: KEY_ORDER.DESC,
    orderBy: 'creditsAmount',
  };
  const [order, setOrder] = useState<{
    order: KEY_ORDER;
    orderBy: string;
  }>(ENTERPRISE_DEFAULT_ORDER);
  const [orderRecharge, setOrderRecharge] = useState<{
    order: KEY_ORDER;
    orderBy: string;
  }>(RECHARGE_DEFAULT_ORDER);
  const { data } = useGetEnterpriseCreditsConsumption(id, {
    page,
    limit: PER_PAGE,
    order: order.order,
    orderBy: order.orderBy,
  });
  const { data: rechargeData } = useGetEnterpriseCreditsTopUp(id, {
    page: pageRecharge,
    limit: RECHARGE_PER_PAGE,
    order: orderRecharge.order,
    orderBy: orderRecharge.orderBy,
  });
  const rows = data?.data.data;

  const getAllowedActions = (code: ConsumptionAction): boolean | undefined => {
    return consumptionAllowedActions?.includes(code);
  };

  const rechargeDataRows = rechargeData?.data.data;
  const isNotFoundConsumptions = rows?.length === 0;
  const isNotFoundRecharges = rechargeDataRows?.length === 0;
  const pagesCountConsumption = data?.data.meta.itemCount;
  const pagesCountRecharge = rechargeData?.data.meta.itemCount;
  const meta = data?.data.meta;
  const metaRecharge = rechargeData?.data.meta;
  const { show, close } = useJConfirmationBar();

  const [isDirty, setIsDirty] = useState(false);
  const [payload, setPayload] = useState(consumptionAllowedActions);
  const {
    mutate: updateAllowedActionsMutation,
    isSuccess: isMutateSettingsSuccess,
  } = useUpdateConsumptionAllowedActions();

  const handleColumnClick = (column: string) => {
    if (order.orderBy === column) {
      // If user clicks on the same column, switch the order
      setOrder((prevOrder) => ({
        order:
          prevOrder.order === KEY_ORDER.ASC ? KEY_ORDER.DESC : KEY_ORDER.ASC,
        orderBy: column,
      }));
    } else {
      // If user clicks on a different column, update order field and use default order
      setOrder({
        order: KEY_ORDER.ASC,
        orderBy: column,
      });
    }
  };
  const handleRechargeColumnClick = (column: string) => {
    if (order.orderBy === column) {
      // If user clicks on the same column, switch the order
      setOrderRecharge((prevOrder) => ({
        order:
          prevOrder.order === KEY_ORDER.ASC ? KEY_ORDER.DESC : KEY_ORDER.ASC,
        orderBy: column,
      }));
    } else {
      // If user clicks on a different column, update order field and use default order
      setOrderRecharge({
        order: KEY_ORDER.ASC,
        orderBy: column,
      });
    }
  };
  const onChangeSetting = (code: ConsumptionAction, value: string) => {
    // set is dirty
    if (!isDirty) {
      setIsDirty(true);
    }

    // Check if the code already exists in payload
    const existingIndex = payload?.findIndex((item) => item === code);

    if (existingIndex !== -1) {
      updateValue(code, value, existingIndex);
    } else {
      addValue(code, value);
    }
  };

  const addValue = (code: ConsumptionAction, value: string) => {
    if (value === 'false') {
      const oldPayload = payload;
      oldPayload?.push(code);
      setPayload(oldPayload);
    }
  };
  const updateValue = (
    code: ConsumptionAction,
    value: string,
    index: number | undefined,
  ) => {
    const updatedPayload = payload;
    if (index !== undefined) {
      if (value === 'true') updatedPayload?.splice(index, 1);
    }
    setPayload(updatedPayload);
  };
  useEffect(() => {
    if (!isDirty) return;
    show({
      onClose: () => {},
      onConfirm: () =>
        updateAllowedActionsMutation({
          id,
          payload: { consumptionAction: payload },
        }),
    });
  }, [isDirty, payload]);
  useEffect(() => {
    return () => close();
  }, []);

  useEffect(() => {
    if (!isMutateSettingsSuccess) return;
    close();
    setIsDirty(false);
  }, [isMutateSettingsSuccess]);
  return (
    <>
      <Jpaper mb={3}>
        <CheckboxSetting
          reset={true}
          defaultValue={
            getAllowedActions(ConsumptionAction.PLAY_CV_VIDEO)
              ? 'false'
              : 'true'
          }
          rank={1}
          label={t('USING_CREDITS_TO_WATCH_CV_VIDEO')}
          items={[
            { label: t('YES'), value: 'true' },
            { label: t('NO'), value: 'false' },
          ]}
          onChange={(value) =>
            onChangeSetting(ConsumptionAction.PLAY_CV_VIDEO, String(value))
          }
        />

        <CheckboxSetting
          reset={true}
          defaultValue={
            getAllowedActions(ConsumptionAction.ACCESS_CANDIDATE_CONTACTS_DATA)
              ? 'false'
              : 'true'
          }
          rank={2}
          label={t('USING_CREDITS_TO_UNLOCK_CONTACT')}
          items={[
            { label: t('YES'), value: 'true' },
            { label: t('NO'), value: 'false' },
          ]}
          onChange={(value) =>
            onChangeSetting(
              ConsumptionAction.ACCESS_CANDIDATE_CONTACTS_DATA,
              String(value),
            )
          }
        />
      </Jpaper>
      <Jpaper mb={3}>
        <JTable
          tableTitle={t('CONSUMPTION_HISTORY') ?? ''}
          isNotFound={isNotFoundConsumptions}
          columns={SPENDING_TABLE_COLS}
          notFoundLabel={t('NO_CONSUMPTION_FOUND') ?? ''}
          total={pagesCountConsumption ?? 0}
          perpage={PER_PAGE}
          nbpages={pagesCountConsumption ?? 0}
          onChangeOrder={(value) => handleColumnClick(value)}
          showDownload={true}
          linkDownload={EndPoints.enterprise.exportCsvConsumptions(id)}
        >
          {rows?.map((row) => (
            <SpendingTableRow row={row} key={row.creditConsumption_id} />
          ))}
        </JTable>
        {meta && (
          <Box mt={2}>
            <JTablePagination
              onPageChange={(value) => {
                setPage(value);
              }}
              meta={meta}
            />
          </Box>
        )}
      </Jpaper>
      <Jpaper mb={3}>
        <JTable
          tableTitle={t('RECHARGE_HISTORY') ?? ''}
          isNotFound={isNotFoundRecharges}
          columns={RECHARGE_TABLE_COLS}
          notFoundLabel={t('NO_RECHARGE_FOUND') ?? ''}
          total={pagesCountRecharge ?? 0}
          perpage={PER_PAGE}
          nbpages={pagesCountRecharge ?? 0}
          onChangeOrder={(value) => handleRechargeColumnClick(value)}
          showDownload={true}
          linkDownload={EndPoints.enterprise.exportCsvTopUp(id)}
        >
          {rechargeDataRows?.map((row) => (
            <RechargeTableRow row={row} key={row.id} />
          ))}
        </JTable>
        {metaRecharge && (
          <Box mt={2}>
            <JTablePagination
              onPageChange={(value) => {
                setPageRecharge(value);
              }}
              meta={metaRecharge}
            />
          </Box>
        )}
      </Jpaper>
    </>
  );
};
export default EnterpriseCredits;
